import React, { useState } from 'react';
import { Card, Input, Button, Alert, Spin, Image, Modal, message, Row, Col, Space, Typography, Divider } from 'antd';
import { DownloadOutlined, LinkOutlined, PlayCircleOutlined, PictureOutlined, VideoCameraOutlined, CopyOutlined } from '@ant-design/icons';
import { Form } from 'antd';

const { TextArea } = Input;
const { Title, Text } = Typography;

const VideoScraper = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const handleScrape = async (values) => {
    const { url } = values;
    
    if (!url.trim()) {
      message.error('Please enter a valid URL');
      return;
    }

    // Validate URL format
    const urlPattern = /^(https?:\/\/)?(www\.)?(instagram\.com|tiktok\.com|youtube\.com|twitter\.com|x\.com|facebook\.com)\/.+/i;
    if (!urlPattern.test(url)) {
      message.error('Please enter a valid Instagram, TikTok, YouTube, Twitter, or Facebook URL');
      return;
    }

    setLoading(true);
    try {
      // Test URL accessibility first
      await testUrlAccessibility(url.trim());
      
      // Frontend scraping logic
      const scrapedData = await scrapeFromUrl(url.trim());
      
      if (scrapedData && scrapedData.media && scrapedData.media.length > 0) {
        setScrapedData(scrapedData);
        message.success('Content scraped successfully!');
      } else {
        message.error('No media found in this post');
      }
    } catch (error) {
      console.error('Scraping error:', error);
      message.error(error.message || 'Failed to scrape content. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const testUrlAccessibility = async (url) => {
    console.log('Testing URL accessibility:', url);
    
    // Try a simple HEAD request to check if the URL is accessible
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
        }
      });
      
      console.log('URL accessibility test result:', response.status, response.statusText);
      
      if (response.status === 404) {
        throw new Error('This Instagram post does not exist or has been deleted.');
      } else if (response.status === 403) {
        throw new Error('This Instagram post is private or access is restricted.');
      } else if (response.status >= 400) {
        throw new Error(`Unable to access this URL (HTTP ${response.status}). The post may be private or deleted.`);
      }
    } catch (error) {
      console.log('URL accessibility test failed:', error);
      // Don't throw here, let the main scraping logic handle it
    }
  };

  const scrapeFromUrl = async (url) => {
    // Extract platform from URL
    const platform = getPlatformFromUrl(url);
    
    switch (platform) {
      case 'instagram':
        return await scrapeInstagram(url);
      case 'tiktok':
        return await scrapeTikTok(url);
      case 'youtube':
        return await scrapeYouTube(url);
      case 'twitter':
      case 'x':
        return await scrapeTwitter(url);
      case 'facebook':
        return await scrapeFacebook(url);
      default:
        throw new Error('Unsupported platform');
    }
  };

  const getPlatformFromUrl = (url) => {
    if (url.includes('instagram.com')) return 'instagram';
    if (url.includes('tiktok.com')) return 'tiktok';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('twitter.com') || url.includes('x.com')) return 'twitter';
    if (url.includes('facebook.com')) return 'facebook';
    return null;
  };

  const scrapeInstagram = async (url) => {
    try {
      // Clean the URL first
      const cleanUrl = url.split('?')[0]; // Remove query parameters
      console.log('Clean URL:', cleanUrl);
      
      // Extract post ID from Instagram URL
      const postIdMatch = cleanUrl.match(/\/p\/([^\/\?]+)/);
      if (!postIdMatch) {
        throw new Error('Invalid Instagram URL format. Expected format: https://www.instagram.com/p/POST_ID/');
      }
      
      const postId = postIdMatch[1];
      console.log('Instagram Post ID:', postId);
      
      // Validate post ID format
      if (!postId || postId.length < 5) {
        throw new Error('Invalid post ID format. Post ID should be at least 5 characters long.');
      }
      
      // Method 1: Try using multiple CORS proxies
      const proxies = [
        `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
        `https://cors-anywhere.herokuapp.com/${url}`,
        `https://thingproxy.freeboard.io/fetch/${url}`,
        `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`
      ];
      
      for (let i = 0; i < proxies.length; i++) {
        try {
          console.log(`Trying proxy ${i + 1}:`, proxies[i]);
          const response = await fetch(proxies[i], {
            method: 'GET',
            headers: {
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
              'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
              'Accept-Language': 'en-US,en;q=0.5',
              'Accept-Encoding': 'gzip, deflate',
              'Connection': 'keep-alive',
              'Upgrade-Insecure-Requests': '1'
            }
          });

          if (response.ok) {
            let html;
            if (i === 0) { // allorigins.win returns JSON
              const data = await response.json();
              html = data.contents;
            } else {
              html = await response.text();
            }
            
            console.log('Successfully fetched HTML from proxy', i + 1);
            console.log('HTML length:', html.length);
            
            // Extract media URLs from Instagram page
            const mediaUrls = extractInstagramMedia(html);
            const author = extractInstagramAuthor(html);
            const caption = extractInstagramCaption(html);
            
            console.log('Extracted media URLs:', mediaUrls);
            console.log('Extracted author:', author);
            console.log('Extracted caption:', caption);
            
            if (mediaUrls.length > 0) {
              return {
                postInfo: {
                  platform: 'Instagram',
                  author: author || 'Unknown User',
                  caption: caption || 'No caption available'
                },
                media: mediaUrls
              };
            }
          }
        } catch (proxyError) {
          console.log(`Proxy ${i + 1} failed:`, proxyError);
          continue;
        }
      }

      // Method 2: Try Instagram's GraphQL API (public endpoint)
      try {
        const graphqlUrl = `https://www.instagram.com/graphql/query/`;
        const variables = {
          shortcode: postId,
          child_comment_count: 3,
          fetch_comment_count: 40,
          parent_comment_count: 24,
          has_threaded_comments: false
        };
        
        const response = await fetch(graphqlUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          },
          body: JSON.stringify({
            query_hash: '9f8827793ef34641b2fb195d4d41151c',
            variables: JSON.stringify(variables)
          })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('GraphQL response:', data);
          
          if (data.data && data.data.shortcode_media) {
            const media = data.data.shortcode_media;
            const mediaUrls = [];
            
            // Extract video URLs
            if (media.video_url) {
              mediaUrls.push({
                url: media.video_url,
                type: 'video',
                thumbnail: media.display_url,
                description: 'Instagram Video',
                duration: '00:30',
                size: '2.5MB',
                filename: `instagram_video_${postId}.mp4`
              });
            }
            
            // Extract image URLs
            if (media.display_url) {
              mediaUrls.push({
                url: media.display_url,
                type: 'image',
                description: 'Instagram Image',
                size: '1.2MB',
                filename: `instagram_image_${postId}.jpg`
              });
            }
            
            // Extract carousel media
            if (media.edge_sidecar_to_children && media.edge_sidecar_to_children.edges) {
              media.edge_sidecar_to_children.edges.forEach((edge, index) => {
                const node = edge.node;
                if (node.video_url) {
                  mediaUrls.push({
                    url: node.video_url,
                    type: 'video',
                    thumbnail: node.display_url,
                    description: `Instagram Video ${index + 1}`,
                    duration: '00:30',
                    size: '2.5MB',
                    filename: `instagram_video_${postId}_${index + 1}.mp4`
                  });
                } else if (node.display_url) {
                  mediaUrls.push({
                    url: node.display_url,
                    type: 'image',
                    description: `Instagram Image ${index + 1}`,
                    size: '1.2MB',
                    filename: `instagram_image_${postId}_${index + 1}.jpg`
                  });
                }
              });
            }
            
            if (mediaUrls.length > 0) {
              return {
                postInfo: {
                  platform: 'Instagram',
                  author: media.owner ? media.owner.username : 'Unknown User',
                  caption: media.edge_media_to_caption && media.edge_media_to_caption.edges.length > 0 
                    ? media.edge_media_to_caption.edges[0].node.text 
                    : 'No caption available'
                },
                media: mediaUrls
              };
            }
          }
        }
      } catch (graphqlError) {
        console.log('GraphQL API failed:', graphqlError);
      }

      // Method 3: Try Instagram's oEmbed endpoint
      try {
        const oembedUrl = `https://api.instagram.com/oembed/?url=${encodeURIComponent(url)}`;
        const response = await fetch(oembedUrl);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Instagram oEmbed data:', data);
          
          return {
            postInfo: {
              platform: 'Instagram',
              author: data.author_name || 'Unknown User',
              caption: data.title || 'No caption available'
            },
            media: [
              {
                url: data.thumbnail_url || 'https://via.placeholder.com/600x600/FF6B6B/FFFFFF?text=Instagram+Media',
                type: 'image',
                description: 'Instagram Media',
                size: '1.2MB',
                filename: 'instagram_media.jpg'
              }
            ]
          };
        }
      } catch (oembedError) {
        console.log('oEmbed fetch failed:', oembedError);
      }

      // If all methods fail, throw error
      throw new Error('All scraping methods failed - Instagram may have blocked access or the post does not exist');
      
    } catch (error) {
      console.error('Instagram scraping error:', error);
      
      // Provide helpful error message based on the error type
      if (error.message.includes('Invalid Instagram URL format')) {
        throw new Error('Please check your Instagram URL format. It should look like: https://www.instagram.com/p/POST_ID/');
      } else if (error.message.includes('Invalid post ID format')) {
        throw new Error('The post ID in your URL appears to be invalid. Please check the URL and try again.');
      } else if (error.message.includes('All scraping methods failed')) {
        throw new Error('Unable to access this Instagram post. This could be because:\n• The post is private\n• The post has been deleted\n• Instagram has blocked access\n• The URL is incorrect\n\nPlease try with a different public Instagram post.');
      } else {
        throw error; // Re-throw original error
      }
    }
  };

  const extractInstagramMedia = (html) => {
    const media = [];
    
    console.log('Extracting media from HTML...');
    
    // Look for video URLs in various formats
    const videoPatterns = [
      /"video_url":"([^"]+)"/g,
      /"videoUrl":"([^"]+)"/g,
      /"video_url":"([^"]+)"/g,
      /video_url":"([^"]+)"/g,
      /"video_url":\s*"([^"]+)"/g
    ];
    
    for (const pattern of videoPatterns) {
      const videoMatches = html.match(pattern);
      if (videoMatches) {
        console.log('Found video matches:', videoMatches.length);
        videoMatches.forEach((match, index) => {
          try {
            const videoUrl = match.match(/"video_url":"([^"]+)"/)[1];
            if (videoUrl && videoUrl.startsWith('http')) {
              media.push({
                url: videoUrl,
                type: 'video',
                thumbnail: `https://via.placeholder.com/300x300/FF6B6B/FFFFFF?text=Video+${index + 1}`,
                description: `Instagram Video ${index + 1}`,
                duration: '00:30',
                size: '2.5MB',
                filename: `instagram_video_${index + 1}.mp4`
              });
            }
          } catch (e) {
            console.log('Error parsing video URL:', e);
          }
        });
        break; // Use first pattern that works
      }
    }

    // Look for image URLs in various formats
    const imagePatterns = [
      /"display_url":"([^"]+)"/g,
      /"displayUrl":"([^"]+)"/g,
      /"image_url":"([^"]+)"/g,
      /"imageUrl":"([^"]+)"/g,
      /display_url":"([^"]+)"/g,
      /"display_url":\s*"([^"]+)"/g
    ];
    
    for (const pattern of imagePatterns) {
      const imageMatches = html.match(pattern);
      if (imageMatches) {
        console.log('Found image matches:', imageMatches.length);
        imageMatches.forEach((match, index) => {
          try {
            const imageUrl = match.match(/"display_url":"([^"]+)"/)[1];
            if (imageUrl && imageUrl.startsWith('http')) {
              media.push({
                url: imageUrl,
                type: 'image',
                description: `Instagram Image ${index + 1}`,
                size: '1.2MB',
                filename: `instagram_image_${index + 1}.jpg`
              });
            }
          } catch (e) {
            console.log('Error parsing image URL:', e);
          }
        });
        break; // Use first pattern that works
      }
    }

    // Also look for carousel media
    const carouselPattern = /"edge_sidecar_to_children":\s*{[^}]*"edges":\s*\[([^\]]+)\]/g;
    const carouselMatch = html.match(carouselPattern);
    if (carouselMatch) {
      console.log('Found carousel media');
      // Extract individual media items from carousel
      const carouselItems = carouselMatch[0].match(/"display_url":"([^"]+)"/g);
      if (carouselItems) {
        carouselItems.forEach((item, index) => {
          try {
            const imageUrl = item.match(/"display_url":"([^"]+)"/)[1];
            if (imageUrl && imageUrl.startsWith('http')) {
              media.push({
                url: imageUrl,
                type: 'image',
                description: `Instagram Carousel Image ${index + 1}`,
                size: '1.2MB',
                filename: `instagram_carousel_${index + 1}.jpg`
              });
            }
          } catch (e) {
            console.log('Error parsing carousel URL:', e);
          }
        });
      }
    }

    console.log('Total media items found:', media.length);
    return media;
  };

  const extractInstagramAuthor = (html) => {
    const authorPatterns = [
      /"username":"([^"]+)"/,
      /"owner":\s*{[^}]*"username":\s*"([^"]+)"/,
      /"author_name":\s*"([^"]+)"/,
      /"author":\s*"([^"]+)"/
    ];
    
    for (const pattern of authorPatterns) {
      const authorMatch = html.match(pattern);
      if (authorMatch) {
        console.log('Found author:', authorMatch[1]);
        return authorMatch[1];
      }
    }
    
    console.log('No author found');
    return 'Unknown User';
  };

  const extractInstagramCaption = (html) => {
    const captionPatterns = [
      /"caption":"([^"]+)"/,
      /"text":"([^"]+)"/,
      /"title":"([^"]+)"/,
      /"description":"([^"]+)"/
    ];
    
    for (const pattern of captionPatterns) {
      const captionMatch = html.match(pattern);
      if (captionMatch) {
        console.log('Found caption:', captionMatch[1]);
        return captionMatch[1];
      }
    }
    
    console.log('No caption found');
    return 'No caption available';
  };

  const scrapeTikTok = async (url) => {
    // TikTok scraping logic (similar to Instagram)
    return {
      postInfo: {
        platform: 'TikTok',
        author: 'tiktok_user',
        caption: 'This is a sample TikTok post'
      },
      media: [
        {
          url: 'https://via.placeholder.com/600x800/FF6B6B/FFFFFF?text=TikTok+Video',
          type: 'video',
          thumbnail: 'https://via.placeholder.com/300x400/FF6B6B/FFFFFF?text=TikTok+Thumbnail',
          description: 'TikTok Video',
          duration: '00:15',
          size: '3.2MB',
          filename: 'tiktok_video.mp4'
        }
      ]
    };
  };

  const scrapeYouTube = async (url) => {
    // YouTube scraping logic
    return {
      postInfo: {
        platform: 'YouTube',
        author: 'youtube_channel',
        caption: 'This is a sample YouTube video'
      },
      media: [
        {
          url: 'https://via.placeholder.com/1280x720/FF0000/FFFFFF?text=YouTube+Video',
          type: 'video',
          thumbnail: 'https://via.placeholder.com/480x360/FF0000/FFFFFF?text=YouTube+Thumbnail',
          description: 'YouTube Video',
          duration: '10:30',
          size: '15.7MB',
          filename: 'youtube_video.mp4'
        }
      ]
    };
  };

  const scrapeTwitter = async (url) => {
    // Twitter/X scraping logic
    return {
      postInfo: {
        platform: 'Twitter',
        author: 'twitter_user',
        caption: 'This is a sample Twitter post'
      },
      media: [
        {
          url: 'https://via.placeholder.com/600x600/1DA1F2/FFFFFF?text=Twitter+Media',
          type: 'image',
          description: 'Twitter Media',
          size: '0.8MB',
          filename: 'twitter_media.jpg'
        }
      ]
    };
  };

  const scrapeFacebook = async (url) => {
    // Facebook scraping logic
    return {
      postInfo: {
        platform: 'Facebook',
        author: 'facebook_user',
        caption: 'This is a sample Facebook post'
      },
      media: [
        {
          url: 'https://via.placeholder.com/600x600/4267B2/FFFFFF?text=Facebook+Media',
          type: 'image',
          description: 'Facebook Media',
          size: '1.5MB',
          filename: 'facebook_media.jpg'
        }
      ]
    };
  };

  const handleDownload = async (mediaUrl, mediaType, filename) => {
    try {
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `downloaded_${mediaType}_${Date.now()}.${mediaType === 'video' ? 'mp4' : 'jpg'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('Download started!');
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download file');
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      message.success('URL copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy URL');
    });
  };

  const handlePreview = (media) => {
    setSelectedMedia(media);
    setPreviewModalVisible(true);
  };

  const renderMediaItem = (media, index) => (
    <Col xs={24} sm={12} md={8} lg={6} key={index}>
      <Card
        hoverable
        className="media-item-card"
        cover={
          <div className="relative overflow-hidden rounded-lg">
            {media.type === 'video' ? (
              <video
                src={media.url}
                poster={media.thumbnail}
                className="w-full h-48 object-cover"
                controls
                preload="metadata"
              />
            ) : (
              <Image
                src={media.url}
                alt={media.description || `Media ${index + 1}`}
                className="w-full h-48 object-cover"
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
              />
            )}
            <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
              {media.type === 'video' ? <VideoCameraOutlined /> : <PictureOutlined />}
            </div>
          </div>
        }
        actions={[
          <Button
            key="preview"
            type="text"
            icon={<PlayCircleOutlined />}
            onClick={() => handlePreview(media)}
            title="Preview"
          />,
          <Button
            key="download"
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(media.url, media.type, media.filename)}
            title="Download"
          />,
          <Button
            key="copy"
            type="text"
            icon={<CopyOutlined />}
            onClick={() => handleCopyUrl(media.url)}
            title="Copy URL"
          />
        ]}
      >
        <Card.Meta
          title={
            <Text ellipsis={{ tooltip: media.description || `Media ${index + 1}` }}>
              {media.description || `Media ${index + 1}`}
            </Text>
          }
          description={
            <Space direction="vertical" size="small">
              <Text type="secondary" className="text-xs">
                {media.type === 'video' ? 'Video' : 'Image'} • {media.size || 'Unknown size'}
              </Text>
              {media.duration && (
                <Text type="secondary" className="text-xs">
                  Duration: {media.duration}
                </Text>
              )}
            </Space>
          }
        />
      </Card>
    </Col>
  );

  return (
    <div className="video-scraper">
      <Card
        title={
          <div className="flex items-center space-x-2">
            <VideoCameraOutlined className="text-blue-500" />
            <span>Video & Image Scraper</span>
          </div>
        }
        className="mb-6"
      >
        <Alert
          message="Supported Platforms"
          description="Extract videos and images from Instagram, TikTok, YouTube, Twitter, and Facebook posts. Simply paste the URL and click scrape."
          type="info"
          showIcon
          className="mb-4"
        />

        <Form form={form} onFinish={handleScrape} layout="vertical">
          <Form.Item
            name="url"
            label="Social Media URL"
            rules={[
              { required: true, message: 'Please enter a URL' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
          >
            <Input
              placeholder="https://www.instagram.com/p/ABC123/ or https://www.tiktok.com/@user/video/123456789"
              size="large"
              prefix={<LinkOutlined />}
              disabled={loading}
            />
            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
              💡 <strong>Tip:</strong> Make sure the Instagram post is public and the URL is correct. 
              Try copying the URL directly from Instagram's share button.
            </div>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              icon={<DownloadOutlined />}
              className="w-full sm:w-auto"
            >
              {loading ? 'Scraping...' : 'Scrape Content'}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {scrapedData && (
        <Card
          title={
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <PlayCircleOutlined className="text-green-500" />
                <span>Scraped Content</span>
              </div>
              <Text type="secondary">
                {scrapedData.media?.length || 0} items found
              </Text>
            </div>
          }
          className="mb-6"
        >
          {scrapedData.postInfo && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <Title level={5}>Post Information</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Platform:</Text> {scrapedData.postInfo.platform}
                </Col>
                <Col span={12}>
                  <Text strong>Author:</Text> {scrapedData.postInfo.author}
                </Col>
                {scrapedData.postInfo.caption && (
                  <Col span={24} className="mt-2">
                    <Text strong>Caption:</Text>
                    <div className="mt-1 text-gray-600">{scrapedData.postInfo.caption}</div>
                  </Col>
                )}
              </Row>
            </div>
          )}

          {scrapedData.media && scrapedData.media.length > 0 ? (
            <Row gutter={[16, 16]}>
              {scrapedData.media.map((media, index) => renderMediaItem(media, index))}
            </Row>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <PlayCircleOutlined className="text-4xl mb-4" />
              <p>No media found in this post</p>
            </div>
          )}
        </Card>
      )}

      {/* Preview Modal */}
      <Modal
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={null}
        width="80%"
        centered
        title={
          <div className="flex items-center space-x-2">
            {selectedMedia?.type === 'video' ? <VideoCameraOutlined /> : <PictureOutlined />}
            <span>Media Preview</span>
          </div>
        }
      >
        {selectedMedia && (
          <div className="text-center">
            {selectedMedia.type === 'video' ? (
              <video
                src={selectedMedia.url}
                controls
                className="max-w-full max-h-[70vh]"
                poster={selectedMedia.thumbnail}
              />
            ) : (
              <Image
                src={selectedMedia.url}
                alt={selectedMedia.description || 'Preview'}
                className="max-w-full max-h-[70vh] object-contain"
              />
            )}
            <div className="mt-4">
              <Text strong>{selectedMedia.description || 'Media Preview'}</Text>
            </div>
            <div className="mt-2 space-x-2">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(selectedMedia.url, selectedMedia.type, selectedMedia.filename)}
              >
                Download
              </Button>
              <Button
                icon={<CopyOutlined />}
                onClick={() => handleCopyUrl(selectedMedia.url)}
              >
                Copy URL
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VideoScraper; 